import type {OutboxItem} from './types';

export interface OutboxExecutorResult {
  summary: string;
}

export interface OutboxExecutorContext {
  sessionId: string;
  /** Resolves a temp id assigned by an earlier queued item to its real id. */
  resolveId: (tempId: string) => string | number | undefined;
  /** Persists a temp→real id mapping before the executor returns. */
  recordIdMapping: (tempId: string, realId: string | number) => Promise<void>;
  /** Bumps the item's claim during a long upload to defend it from reclaim. */
  heartbeat: () => Promise<void>;
}

export type OutboxExecutor = (
  payload: unknown,
  clientSubmissionId: string,
  ctx: OutboxExecutorContext
) => Promise<OutboxExecutorResult>;

// Thrown when a temp id an executor depends on was never mapped. Classified via
// `findParent`: transient while the parent may still succeed, else deterministic.
export class DependsOnFailedItemError extends Error {
  readonly tempId: string;
  /** Locates the parent outbox item the temp id depends on (domain-supplied). */
  readonly findParent?: () => Promise<OutboxItem | undefined>;

  constructor(
    tempId: string,
    findParent?: () => Promise<OutboxItem | undefined>
  ) {
    super(`Depends on a failed or unsynced item (temp id "${tempId}").`);
    this.name = 'DependsOnFailedItemError';
    this.tempId = tempId;
    this.findParent = findParent;
  }
}

/**
 * Thrown for a permanently un-retryable condition (e.g. a captured blob that no
 * longer exists). Classified as deterministic so it surfaces, not retried.
 */
export class DeterministicExecutorError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DeterministicExecutorError';
  }
}

// Registry of outbox executors by `kind`; domain modules register their own
// kinds via `registerOutboxExecutor`.
const executors = new Map<string, OutboxExecutor>();
const barrierKinds = new Set<string>();

export interface RegisterOutboxExecutorOptions {
  /** A barrier item must not run until every earlier item in its session has
   * succeeded — prevents e.g. a "complete" transition landing before its data. */
  barrier?: boolean;
}

export const registerOutboxExecutor = (
  kind: string,
  executor: OutboxExecutor,
  options?: RegisterOutboxExecutorOptions
): void => {
  executors.set(kind, executor);
  if (options?.barrier) barrierKinds.add(kind);
};

export const getOutboxExecutor = (kind: string): OutboxExecutor | undefined =>
  executors.get(kind);

export const isBarrierKind = (kind: string): boolean => barrierKinds.has(kind);
